import React, {
  useContext,
  ReactNode,
  useLayoutEffect,
  createContext,
} from 'react';
import { PopoverSize } from './Popover';

interface PopoverContextValue {
  setScreenTitle: (screenTitle?: ReactNode) => void;
  setScreenSize: (screenSize?: PopoverSize) => void;
  setScreenNoHorizontalPadding: (noHorizontalPadding?: boolean) => void;
  setScreenTestId: (testId?: string) => void;
  currentScreen?: number;
}

interface PopoverScreenProps {
  id: number;
  title?: ReactNode;
  size?: PopoverSize;
  noHorizontalPadding?: boolean;
  testId?: string;
}

export const PopoverContext = createContext<PopoverContextValue>({
  setScreenTitle: () => {},
  setScreenSize: () => {},
  setScreenNoHorizontalPadding: () => {},
  setScreenTestId: () => {},
  currentScreen: undefined,
});

export const PopoverScreen: React.FunctionComponent<PopoverScreenProps> = ({
  id,
  children,
  title,
  size,
  noHorizontalPadding,
  testId,
}) => {
  const {
    currentScreen,
    setScreenTitle,
    setScreenSize,
    setScreenNoHorizontalPadding,
    setScreenTestId,
  } = useContext(PopoverContext);

  // Update the title of the popover, based on the title prop of the selected
  // PopoverScreen
  useLayoutEffect(() => {
    if (currentScreen === id) {
      setScreenTitle(title);
    }
  }, [currentScreen, id, title, setScreenTitle]);

  // Update the size of the popover, based on the size prop of the selected
  // PopoverScreen
  useLayoutEffect(() => {
    if (currentScreen === id) {
      setScreenSize(size);
    }
  }, [currentScreen, id, size, setScreenSize]);

  // Update the size of the popover, based on the noHorizontalPadding prop of the selected
  // PopoverScreen
  useLayoutEffect(() => {
    if (currentScreen === id) {
      setScreenNoHorizontalPadding(noHorizontalPadding);
    }
  }, [currentScreen, id, noHorizontalPadding, setScreenNoHorizontalPadding]);

  // Update the test id of the popover, based on the testId prop of the selected PopoverScreen
  useLayoutEffect(() => {
    if (currentScreen === id) {
      setScreenTestId(testId);
    }
  }, [currentScreen, id, testId, setScreenTestId]);

  if (currentScreen !== id) {
    return null;
  }

  return <>{children}</>;
};
